import z from 'zod';

const workOrderSchema = z.object({
  id: z.number(),
  frame: z.object({
    id: z.number(),
    name: z.string(),
  }),
  assigned_users: z.array(
    z.object({
      id: z.number(),
      display_name: z.string(),
    }),
  ),
  created_at: z.date(),
  updated_at: z.date(),
  work_order_identifier: z.string(),
  status: z.string(),
  description: z.string().optional(),
  total_quantity: z.number(),
  assembled_quantity: z.number(),
  glazed_quantity: z.number(),
  packed_quantity: z.number(),
  shipped_quantity: z.number(),
  installed_quantity: z.number(),
  project: z.number(),
  release: z.number(),
});

export const post = {
  body: z.object({
    frames: z
      .array(
        z.object({
          frame: z.coerce.number(),
          description: z.string().optional(),
        }),
      )
      .min(1, { error: 'At least one frame must be selected' }),
    assigned_users: z.array(z.coerce.number()).min(1, {
      error: 'At least one user must be assigned to the work order',
    }),
  }),
  response: workOrderSchema,
  params: z.object({
    project_id: z.coerce.number(),
    release_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postSchema = z.object(post);
export type Post = z.infer<typeof postSchema>;

const get = {
  body: z.never(),
  response: z.array(workOrderSchema),
  params: z.object({
    project_id: z.coerce.number(),
    release_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getSchema = z.object(get);
export type Get = z.infer<typeof getSchema>;

export const bulkAssign = {
  body: z.object({
    workorder_ids: z.array(z.coerce.number()).min(1, {
      error: 'At least one work order must be selected',
    }),
    assigned_users: z.array(z.coerce.number()),
    mode: z.enum(['replace']).optional(),
  }),
  response: z.array(workOrderSchema),
  params: z.object({
    project_id: z.coerce.number(),
    release_id: z.coerce.number(),
  }),
  query: z.never(),
};
const bulkAssignSchema = z.object(bulkAssign);
export type BulkAssign = z.infer<typeof bulkAssignSchema>;

const checklistAnswerSchema = z.object({
  item: z.string().optional(),
  checked: z.boolean().optional(),
  status: z.enum(['PASS', 'FAIL', 'NA']).optional(),
  notes: z.string().optional(),
});

const bulkCompleteResultSchema = z.object({
  frame_id: z.number().optional(),
  work_order_id: z.number().optional(),
  step: z.string().optional(),
  quantity_completed: z.number().optional(),
  remaining_quantity: z.number().optional(),
  status: z.string().optional(),
  success: z.boolean(),
  error: z.unknown().optional(),
  inventory_result: z.unknown().optional(),
  // Supervisor override + assignment context
  requires_override: z.boolean().optional(),
  assigned_user_names: z.array(z.string()).optional(),
  override_used: z.boolean().optional(),
  override_reason: z.string().nullable().optional(),
  completed_by: z.string().nullable().optional(),
  error_code: z.string().optional(),
  missing_checklist_items: z.array(z.string()).optional(),
});

export const bulkComplete = {
  body: z.object({
    step: z.enum([
      'ASSEMBLY',
      'GLAZING',
      'PACKING',
      'SHIPPING',
      'INSTALLATION',
    ]),
    allow_partial_success: z.boolean().optional(),
    items: z
      .array(
        z.object({
          frame_id: z.coerce.number(),
          work_order_id: z.coerce.number().optional(),
          quantity: z.coerce.number().min(1),
          checklist_answers: z.array(checklistAnswerSchema).optional(),
          qc_event_id: z.coerce.number().nullable().optional(),
          qc_result: z.enum(['PASS', 'FAIL', 'INFO']).optional(),
          completion_context: z.record(z.string(), z.unknown()).optional(),
          // Supervisor override: explicit per-item opt-in + required reason
          override_assignment: z.boolean().optional(),
          override_reason: z.string().optional(),
        }),
      )
      .min(1),
  }),
  response: z.object({
    detail: z.string(),
    results: z.array(bulkCompleteResultSchema),
  }),
  params: z.object({
    project_id: z.coerce.number(),
    release_id: z.coerce.number(),
  }),
  query: z.never(),
};
const bulkCompleteSchema = z.object(bulkComplete);
export type BulkComplete = z.infer<typeof bulkCompleteSchema>;
